import { Injectable, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from './config.service';
import {
  AffordabilityServiceState,
  AsantoAffordabilityResponse,
  AsantoQuoteResult,
  IUiAffordabilityModel,
  IUiApplicant,
  IUiApplicantAge,
} from '../models';
import { getAsantoMapperFunc } from '../helpers/mapper-helpers';
import { LghfAnalyticsService } from './lghf-analytics.service';
import { Subject } from 'rxjs';
import { Customer } from '../models/lghf-email';
import { toEmailRequest } from '../helpers/mapper-helpers/resi-email';
import { Channel } from '../models/channels';

@Injectable({
  providedIn: 'root',
})
export class AffordabilityService {
  private readonly httpClient = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly lghfAnalyticsService = inject(LghfAnalyticsService);

  public model = this.initialModel;
  public form = new UntypedFormGroup({});
  public showExpenditureBtl = false;
  public isTopSlicing = false;
  public state = AffordabilityServiceState.NotStarted;
  public result: number;
  public monthlyAmount: number;
  public quoteRef: string;
  public edit: boolean = false;
  public newSearch: boolean = false;
  public customers: Customer[] = [];
  public resultTopMsg: string = null;
  public resultBtmMsg: string = null;
  public printResults: boolean = false;
  public printInputs: boolean = false;
  public isSendEmail: boolean = false;
  public resultLoanAmountText: string = null;
  private window: Window;

  public constructor() {
    this.window = window;
  }

  private get initialModel(): IUiAffordabilityModel {
    return {
      propertyAndLoan: {},
      applicants: {
        numberOfApplicants: 1,
        financialDependants: false,
        applicantAges: [
          {
            applicantNumber: 1,
          },
        ],
      },
      incomeAndExpenditure: {
        applicants: [{ ...this.initialApplicant, applicantNumber: 1 }],
      },
    } as IUiAffordabilityModel;
  }

  private get initialApplicant() {
    return {
      primaryIncome: {
        employmentStatus: undefined,
        grossAnnualIncome: '0',
      },
      definedContribution: [{}],
      annualNonEscalatingAnnuity: [{}],
      annualEscalatingAnnuity: [{}],
      definedBenefitFinalSalaryPension: [{}],
      forecastDefinedBenefitFinalSalaryPension: [{}],
      currentDrawdownPensionFundValue: [{}],
    } as IUiApplicant;
  }

  public get hasPersonalDetails(): boolean {
    return this.customers.length > 0;
  }

  public addAdditionalBenefitField(applicantIndex: number, fieldType: string) {
    this.model.incomeAndExpenditure.applicants[applicantIndex][fieldType].push({});
    this.model = { ...this.model };
  }

  public deleteAdditionalBenefitField(applicantIndex: number, fieldType: string, removeIndex: number) {
    this.model.incomeAndExpenditure.applicants[applicantIndex][fieldType].splice(removeIndex, 1);
    this.model = { ...this.model };
  }

  public updateModel(model = {}) {
    this.model = { ...this.model, ...model };
  }

  public addRioFields(key: string) {
    this.model.incomeAndExpenditure.applicants.forEach((applicant) => {
      applicant[key].push({});
    });
  }

  public removeRioFields(key: string, removeIndex) {
    this.model.incomeAndExpenditure.applicants.forEach((applicant) => {
      applicant[key] = applicant[key].filter((answer, index) => index !== removeIndex);
    });
  }

  public resetModel() {
    this.model = this.initialModel;
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  public addApplicant() {
    this.model.applicants.applicantAges.push({} as IUiApplicantAge);
    this.model.applicants.applicantAges.forEach((applicant, index) => (applicant.applicantNumber = index + 1));
  }

  public addApplicantIncome() {
    this.model.incomeAndExpenditure.applicants.push(this.initialApplicant);
    this.model.incomeAndExpenditure.applicants.forEach((applicant, index) => (applicant.applicantNumber = index + 1));
  }

  public getResults(lendingType: 'btl' | 'resi', affiliateCID: string, trafficSource: string, channel: Channel) {
    this.state = AffordabilityServiceState.Loading;

    const url = this.configService.config.asantoUrl[lendingType];
    this.result = 0;
    this.monthlyAmount = 0;
    const map = getAsantoMapperFunc(lendingType);
    const resultSubject = new Subject();
    this.httpClient.post<AsantoAffordabilityResponse>(url, map(this.model)).subscribe((res) => {
      if (res.quotes?.length) {
        this.state = AffordabilityServiceState.Complete;
        const quote = res.quotes[0];
        this.result = this.getResultValue(quote, channel);
        this.monthlyAmount = this.getMonthlyAmountValue(quote, channel);
        this.lghfAnalyticsService
          .callToAction('result', lendingType, this.result, this.model.propertyAndLoan.mortgageTerm);
        this.quoteRef = quote.quote_number;
        // make quote reference accessible in console
        this.window['quoteReference'] = this.quoteRef;
        this.setResultsPageMsg(this.result, this.model.propertyAndLoan.loanAmount);
        this.resultLoanAmountText = this.result >= this.model.propertyAndLoan.loanAmount
          ? 'Loan Amount'
          : 'Maximum Affordable Loan Amount';

        if (channel === Channel.Customer && this.isSendEmail) {
          this.isSendEmail = false;
          const emailUrl = this.configService.config.lghfEmailUrl;
          const emailBody = toEmailRequest(this.model, this.customers, quote, affiliateCID, trafficSource);
          this.httpClient.post<AsantoAffordabilityResponse>(emailUrl, emailBody).subscribe();
        }
      } else {
        this.state = AffordabilityServiceState.Errored;
        // eslint-disable-next-line no-console
        console.warn('Something went wrong: got no quotes from Asanto.');
      }

      resultSubject.next(true);
      resultSubject.complete();
    });

    return resultSubject;
  }

  public getMonthlyAmountValue(quote: AsantoQuoteResult, channel: Channel) {
    if (channel === Channel.Customer) {
      return (
        Math.min(quote.maximum_affordable_loan_amount, this.model.propertyAndLoan.loanAmount) * quote.interest_rate
      ) / 100 / 12;
    }
    return quote.monthly_payment ?? 0;
  }

  public getResultValue(quote: AsantoQuoteResult, channel: Channel) {
    if (channel === Channel.Customer) {
      return Math.min(quote.maximum_affordable_loan_amount, this.model.propertyAndLoan.loanAmount);
    }
    return quote.maximum_affordable_loan_amount;
  }

  public setResultsPageMsg(actualLoanAmount: number, expectedLoanAmount: number) {
    if (actualLoanAmount >= expectedLoanAmount) {
      this.resultTopMsg = `Good news, based on the information you've provided we may be able to
        lend you the amount you're looking for.`;
      this.resultBtmMsg = `The mortgage amount is based on the information you’ve provided and doesn’t
        commit us to offer you a mortgage. The exact amount we’ll lend will be subject to a credit reference check,
        confirmation of income, an affordability assessment and the value and suitability of the property.
        If any of these details change then the mortgage amount we’ll offer may change.<br>
        The calculator gives an estimate of the potential amount you could borrow but is not a guarantee of lending.
        Additional calculations and evidence will be required to fully assess how much you can afford to pay.
        This may reduce the amount we would be prepared to lend.<br>
        <strong>Think carefully before securing a loan against your home.
        As a last resort, your home may be repossessed if you don't keep up with payments.</strong>`;
    } else if (actualLoanAmount > 0) {
      this.resultTopMsg = `Based on the information you’ve provided, the maximum we would be able to
        lend you is:`;
      this.resultBtmMsg = `This payment is fixed for the life of the loan, and will not increase. The mortgage amount
        is based on the information you’ve provided and doesn’t commit us to offer you a mortgage.
        The exact amount we’ll lend will be subject to a credit reference check, confirmation of income,
        an affordability assessment and the value and suitability of the property. If any of these details change then
        the mortgage amount we’ll offer may change.<br>
        The calculator gives an estimate of the potential amount you could borrow but is not a guarantee of lending.
        Additional calculations and evidence will be required to fully assess how much you can afford to pay.
        This may reduce the amount we would be prepared to lend.<br>
        <strong>Think carefully before securing a loan against your home.
        As a last resort, your home may be repossessed if you don't keep up with payments.</strong>`;
    } else {
      this.resultTopMsg = `Based on the information you’ve provided, you wouldn’t be able to release equity with
        our Retirement Interest Only Mortgage.<br> However you may be eligible to borrow money with an Optional
        Payment Lifetime Mortgage which is also a loan secured on your home that lets you make monthly interest
        payments, without having to pass any affordability checks.`;
      this.resultBtmMsg = null;
    }
  }
}
